import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Download, Truck, User, MapPin, Package, Clock } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { formatDate } from '../../lib/utils';
import api from '../../api/clients';
import { toast } from 'sonner';

interface RemitoData {
  id: number;
  destinatarioNombre: string;
  destinatarioDocumento?: string;
  destinatarioTelefono?: string;
  destinatarioDireccion: string;
  destinatarioLocalidad?: string;
  paqueteContenido: string;
  notasAdicionales?: string;
  pdfUrl?: string;
  fechaGeneracion: string;
  remitenteNombre?: string;
  remitenteDireccion?: string;
}

export function ShippingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [remito, setRemito] = useState<RemitoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRemito = async () => {
      try {
        const response = await api.get(`/shipping/${id}`);
        if (response.data.success) {
          setRemito(response.data.data);
        } else {
          toast.error('Remito no encontrado');
          navigate('/remitos');
        }
      } catch (error) {
        toast.error('Error al cargar el remito');
        navigate('/remitos');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadRemito();
    }
  }, [id, navigate]);

  const handleDownloadPDF = async () => {
    if (!remito?.pdfUrl) {
      toast.info('Generando PDF...');
      try {
        const response = await api.post(`/shipping/${id}/pdf`);
        if (response.data.success) {
          window.open(response.data.pdfUrl, '_blank');
          toast.success('PDF generado');
        }
      } catch (error) {
        toast.error('Error al generar el PDF');
      }
      return;
    }
    window.open(remito.pdfUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando remito...</div>
      </div>
    );
  }

  if (!remito) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p>Remito no encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/remitos')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Remito de Envío</h1>
            <p className="text-sm text-gray-500">ID: #{remito.id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadPDF}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {remito.pdfUrl ? (
              <>
                <Download size={18} />
                Descargar PDF
              </>
            ) : (
              <>
                <Printer size={18} />
                Generar PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tarjeta del remito */}
      <Card className="p-6 max-w-2xl mx-auto">
        <div className="space-y-6">
          {/* Remitente */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <Truck size={16} className="inline mr-2" />
              Remitente
            </h3>
            <p className="font-medium text-gray-800">
              {remito.remitenteNombre || 'MI TECNO HOGAR'}
            </p>
            <p className="text-sm text-gray-500">
              {remito.remitenteDireccion || 'San Andrés de Giles, Buenos Aires, CP 6720'}
            </p>
          </div>

          {/* Destinatario */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <User size={16} className="inline mr-2" />
              Destinatario
            </h3>
            <p className="font-medium text-gray-800">{remito.destinatarioNombre}</p>
            {remito.destinatarioDocumento && (
              <p className="text-sm text-gray-500">Documento: {remito.destinatarioDocumento}</p>
            )}
            {remito.destinatarioTelefono && (
              <p className="text-sm text-gray-500">Teléfono: {remito.destinatarioTelefono}</p>
            )}
            <div className="flex items-start gap-2 mt-1">
              <MapPin size={16} className="text-gray-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">{remito.destinatarioDireccion}</p>
                {remito.destinatarioLocalidad && (
                  <p className="text-sm text-gray-500 font-medium">
                    {remito.destinatarioLocalidad}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contenido */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <Package size={16} className="inline mr-2" />
              Contenido
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap">{remito.paqueteContenido}</p>
            {remito.notasAdicionales && (
              <div className="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700">
                  <span className="font-semibold">Nota:</span> {remito.notasAdicionales}
                </p>
              </div>
            )}
          </div>

          {/* Fecha */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              <Clock size={16} className="inline mr-2" />
              Fecha de generación
            </h3>
            <p className="text-gray-700">{formatDate(remito.fechaGeneracion)}</p>
          </div>

          {/* Estado del PDF */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs rounded-full ${remito.pdfUrl ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {remito.pdfUrl ? 'PDF Generado' : 'PDF Pendiente'}
              </span>
              {remito.pdfUrl && (
                <a
                  href={remito.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Ver PDF
                </a>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}